<template>
  <div>
    <!-- Loading Skeleton -->
    <div v-if="loading">
      <v-skeleton-loader type="heading" class="mb-4"></v-skeleton-loader>
      <v-skeleton-loader type="table" class="mb-4"></v-skeleton-loader>
    </div>

    <div v-else class="mb-4">
      <div class="d-flex align-center mb-3">
        <h3 class="language-config-title mr-3">Tax Prefix Patterns</h3>
        <span class="text-caption">{{ taxPrefixPatterns.length }} Pattern(s)</span>
        <v-spacer></v-spacer>
        <v-btn color="#4E3982" variant="outlined" style="height: 40px; border-radius: 12px; text-transform: none" @click="addTaxPrefixPattern" :disabled="!canAdd">
          <v-icon start size="small">mdi-plus</v-icon>
          Add Pattern
        </v-btn>
      </div>

      <v-data-table
        :headers="taxPrefixPatternHeaders"
        :items="taxPrefixPatterns"
        item-value="uid"
        v-model:expanded="expandedTaxPrefixPatterns"
        show-expand
        :items-per-page="10"
        density="comfortable"
        no-data-text="No patterns yet — click Add Pattern to create one"
      >
        <template #[`item.patternNumber`]="{ item }">
          {{ taxPrefixPatterns.indexOf(item) + 1 }}
        </template>

        <template #[`item.patternName`]="{ item }">
          <input v-model="item.patternName" placeholder="Enter pattern name" class="custom-input" :disabled="!canEdit" />
        </template>

        <template #[`item.steps`]="{ item }">
          <v-chip size="small" color="#4E3982" variant="tonal">{{ item.steps.length }} step(s)</v-chip>
        </template>

        <template #[`item.effectiveStartDate`]="{ item }">
          <input v-model="item.effectiveStartDate" type="datetime-local" step="60" class="custom-input" :disabled="!canEdit" />
        </template>

        <template #[`item.effectiveEndDate`]="{ item }">
          <input v-model="item.effectiveEndDate" type="datetime-local" step="60" class="custom-input" :class="{ 'input-error': !validateDateRange(item.effectiveStartDate, item.effectiveEndDate) }" :disabled="!canEdit" />
        </template>

        <template #[`item.isActive`]="{ item }">
          <v-switch inset class="ma-0" v-model="item.isActive" color="#4E3982" hide-details :disabled="!canEdit"></v-switch>
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn icon size="small" variant="plain" color="error" @click="removeTaxPrefixPattern(item)" :disabled="!canDelete">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>

        <template #expanded-row="{ columns, item }">
          <tr>
            <td :colspan="columns.length" class="pa-4" style="background-color: #fbfaff">
              <div class="d-flex align-center mb-2">
                <h4>Steps</h4>
                <v-spacer></v-spacer>
                <v-btn size="small" color="#4E3982" variant="outlined" style="text-transform: none" @click="addTaxPrefixStep(item)" :disabled="!canEdit">
                  <v-icon start size="small">mdi-plus</v-icon>
                  Add Step
                </v-btn>
              </div>

              <v-table density="compact" style="background: transparent">
                <thead>
                  <tr>
                    <th style="width: 55px">Step</th>
                    <th style="width: 120px">Duration (sec)</th>
                    <th v-for="lang in languages" :key="lang.key">{{ lang.label }} <span style="color: red">*</span></th>
                    <th style="width: 120px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(step, sIdx) in item.steps" :key="step.uid">
                    <td class="text-center" style="vertical-align: top; padding-top: 14px">{{ sIdx + 1 }}</td>
                    <td style="vertical-align: top; padding-top: 10px">
                      <input type="number" min="1" v-model.number="step.durationSec" class="custom-input" :disabled="!canEdit" />
                    </td>

                    <td v-for="lang in languages" :key="lang.key" class="pa-2" style="min-width: 220px; vertical-align: top">
                      <textarea
                        v-model="step[lang.textProp]"
                        placeholder="Text (required)"
                        class="custom-input mb-1"
                        :class="{ 'input-error': !step[lang.textProp]?.trim() }"
                        rows="2"
                        style="resize: none"
                        :disabled="!canEdit"
                      ></textarea>

                      <!-- Default Voice (required) + Gemini Voice (optional) -->
                      <template v-for="variant in variants" :key="variant.key">
                        <div class="text-caption text-medium-emphasis" :style="variant.key === 'default' ? 'margin-top: 4px' : 'margin-top: 6px'">
                          {{ variant.label }} <span v-if="variant.required" style="color: red">*</span>
                        </div>

                        <div v-if="step[mp3Prop(lang.key, variant.key)].fileName" class="d-flex align-center" style="gap: 4px">
                          <v-icon :color="variant.color" size="16">mdi-file-music</v-icon>
                          <span class="tax-prefix-file-name-col" style="max-width: 110px">{{ step[mp3Prop(lang.key, variant.key)].fileName }}</span>
                          <v-btn
                            icon
                            size="x-small"
                            variant="tonal"
                            :color="variant.color"
                            @click="playTaxPrefixStepMp3(item, sIdx, lang.key, variant.key)"
                            :title="taxPrefixVoicePlaying === `${item.uid}_${sIdx}_${lang.key}_${variant.key}` ? 'Stop' : 'Play'"
                          >
                            <v-icon size="14">{{ taxPrefixVoicePlaying === `${item.uid}_${sIdx}_${lang.key}_${variant.key}` ? 'mdi-stop' : 'mdi-play' }}</v-icon>
                          </v-btn>
                          <v-btn icon size="x-small" variant="plain" color="error" @click="removeTaxPrefixStepMp3(item, sIdx, lang.key, variant.key)" :disabled="!canEdit" title="Remove">
                            <v-icon size="14">mdi-delete</v-icon>
                          </v-btn>
                        </div>
                        <div
                          v-else
                          class="tax-step-upload-box"
                          @click="canEdit ? triggerTaxPrefixStepMp3Upload(item, sIdx, lang.key, variant.key) : null"
                          :style="!canEdit ? 'opacity: 0.6; cursor: not-allowed; pointer-events: none' : ''"
                        >
                          <v-icon :color="variant.uploadIconColor" size="18">mdi-upload</v-icon>
                          <span>Upload MP3 <span v-if="variant.required" style="color: red">*</span></span>
                        </div>
                      </template>
                    </td>

                    <td style="vertical-align: top; padding-top: 10px; white-space: nowrap">
                      <v-btn icon size="x-small" variant="plain" @click="moveTaxPrefixStep(item, sIdx, -1)" :disabled="sIdx === 0 || !canEdit" title="Move up">
                        <v-icon size="16">mdi-arrow-up</v-icon>
                      </v-btn>
                      <v-btn icon size="x-small" variant="plain" @click="moveTaxPrefixStep(item, sIdx, 1)" :disabled="sIdx === item.steps.length - 1 || !canEdit" title="Move down">
                        <v-icon size="16">mdi-arrow-down</v-icon>
                      </v-btn>
                      <v-btn icon size="x-small" variant="plain" color="error" @click="removeTaxPrefixStep(item, sIdx)" :disabled="!canEdit" title="Remove step">
                        <v-icon size="16">mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                  <tr v-if="item.steps.length === 0">
                    <td :colspan="languages.length + 3" class="text-center text-caption py-4">No steps yet — click "Add Step".</td>
                  </tr>
                </tbody>
              </v-table>
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>

    <input ref="taxPrefixStepMp3Input" type="file" accept=".mp3,audio/mpeg" style="display: none" @change="handleTaxPrefixStepMp3Upload" />
  </div>
</template>

<script>
import moment from 'moment';

// The three standard languages, in column order.
const LANGUAGES = [
  { key: 'thai', label: 'Thai', suffix: 'Thai', textProp: 'textThai' },
  { key: 'english', label: 'English', suffix: 'English', textProp: 'textEnglish' },
  { key: 'chinese', label: 'Chinese', suffix: 'Chinese', textProp: 'textChinese' },
];

// Each language carries two voice slots: the required default voice and an optional
// Gemini voice — same split as Genie Prefix.
const VARIANTS = [
  { key: 'default', label: 'Default Voice', required: true, color: '#4E3982', uploadIconColor: '#c62828' },
  { key: 'gemini', label: 'Gemini Voice', required: false, color: '#4285F4', uploadIconColor: '#4285F4' },
];

// Every (language, variant) pair — used wherever all six MP3 slots must be walked.
const MP3_SLOTS = LANGUAGES.flatMap((lang) =>
  VARIANTS.map((variant) => ({
    language: lang.key,
    label: lang.label,
    variant: variant.key,
    required: variant.required,
    prop: variant.key === 'gemini' ? `geminiMp3${lang.suffix}` : `mp3${lang.suffix}`,
    uploadField: variant.key === 'gemini' ? `tax_gemini_${lang.key}` : `tax_${lang.key}`,
  }))
);

export default {
  name: 'TaxPrefixTab',
  props: {
    mobileConfigurationId: {
      type: Number,
      default: null,
    },
    canAdd: {
      type: Boolean,
      default: false,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      languages: LANGUAGES,
      variants: VARIANTS,
      taxPrefixPatterns: [],
      expandedTaxPrefixPatterns: [],
      taxPrefixPatternsToDelete: [],
      taxPrefixStepMp3UploadTarget: null,
      taxPrefixVoicePlaying: null,
      taxPrefixVoiceAudio: null,
    };
  },
  computed: {
    taxPrefixPatternHeaders() {
      return [
        { title: '#', key: 'patternNumber', sortable: false, width: '60px' },
        { title: 'Pattern Name', key: 'patternName', sortable: false },
        { title: 'Steps', key: 'steps', sortable: false, width: '110px' },
        { title: 'Effective Start', key: 'effectiveStartDate', sortable: false, width: '210px' },
        { title: 'Effective End', key: 'effectiveEndDate', sortable: false, width: '210px' },
        { title: 'Active', key: 'isActive', sortable: false, width: '90px' },
        { title: '', key: 'actions', sortable: false, width: '70px' },
      ];
    },
  },
  watch: {
    mobileConfigurationId: {
      immediate: true,
      handler(value) {
        if (value) this.loadTaxPrefixPatterns();
      },
    },
  },
  beforeUnmount() {
    this.stopTaxPrefixVoiceMp3();
    this.taxPrefixPatterns.forEach((pattern) => pattern.steps.forEach((step) => this.revokeTaxPrefixStepBlobUrls(step)));
  },
  methods: {
    // ===== Shared helpers (mirror the parent page's implementations) =====

    createTaxPrefixUid() {
      return `tpx_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    },

    isApiSuccess(response) {
      return !!response && (response.Status === 'Successful' || response.status === 0);
    },

    validateDateRange(startDate, endDate) {
      if (!startDate || !endDate) return true;
      return endDate >= startDate;
    },

    formatDateForAPI(dateString) {
      if (!dateString) return null;
      return moment(dateString).format('YYYY-MM-DDTHH:mm:ss');
    },

    formatDateForInput(dateString) {
      if (!dateString) return '';
      return moment(dateString).format('YYYY-MM-DDTHH:mm');
    },

    // Step view-model property holding a given language + variant's MP3 slot.
    mp3Prop(language, variant = 'default') {
      const slot = MP3_SLOTS.find((s) => s.language === language && s.variant === variant);
      return slot ? slot.prop : null;
    },

    // ===== Load =====

    loadTaxPrefixPatterns() {
      if (!this.mobileConfigurationId) return;
      this.loading = true;

      this.$call.GetTaxPrefixPatterns(
        this.mobileConfigurationId,
        (response) => {
          this.loading = false;
          const data = response?.Data || response?.data;
          this.taxPrefixPatterns = this.mapTaxPrefixPatterns(Array.isArray(data) ? data : []);
          this.expandedTaxPrefixPatterns = [];
          this.taxPrefixPatternsToDelete = [];
        },
        (error) => {
          this.loading = false;
          console.error('Error loading tax prefix patterns:', error);
          this.taxPrefixPatterns = [];
        }
      );
    },

    mapTaxPrefixPatterns(apiPatterns = []) {
      return apiPatterns.map((pattern) => ({
        uid: this.createTaxPrefixUid(),
        id: pattern.id,
        patternName: pattern.patternName || '',
        effectiveStartDate: this.formatDateForInput(pattern.effectiveStartDate) || '',
        effectiveEndDate: this.formatDateForInput(pattern.effectiveEndDate) || '',
        isActive: pattern.isActive !== undefined ? pattern.isActive : true,
        isNew: false,
        createdAt: pattern.createdAt || null,
        steps: (pattern.steps || []).map((step) => ({
          uid: this.createTaxPrefixUid(),
          id: step.id,
          durationSec: step.durationSec || 3,
          textThai: step.textThai || '',
          textEnglish: step.textEnglish || '',
          textChinese: step.textChinese || '',
          mp3Thai: this.mapSavedTaxPrefixMp3(step.mp3ThaiFileName, step.mp3ThaiUrl),
          mp3English: this.mapSavedTaxPrefixMp3(step.mp3EnglishFileName, step.mp3EnglishUrl),
          mp3Chinese: this.mapSavedTaxPrefixMp3(step.mp3ChineseFileName, step.mp3ChineseUrl),
          geminiMp3Thai: this.mapSavedTaxPrefixMp3(step.geminiMp3ThaiFileName, step.geminiMp3ThaiUrl),
          geminiMp3English: this.mapSavedTaxPrefixMp3(step.geminiMp3EnglishFileName, step.geminiMp3EnglishUrl),
          geminiMp3Chinese: this.mapSavedTaxPrefixMp3(step.geminiMp3ChineseFileName, step.geminiMp3ChineseUrl),
          createdAt: step.createdAt || null,
        })),
      }));
    },

    // A saved file name without a URL means the MP3 never reached blob storage (the first save
    // persists the name, the URL only lands after a successful upload). Show the slot as empty so
    // the failed upload is visible and validation asks for the file again.
    mapSavedTaxPrefixMp3(fileName, url) {
      if (!url) return { file: null, fileName: '', url: '' };
      return { file: null, fileName: fileName || '', url };
    },

    // ===== Pattern / step editing =====

    createEmptyTaxPrefixStep() {
      return {
        uid: this.createTaxPrefixUid(),
        id: null,
        durationSec: 3,
        textThai: '',
        textEnglish: '',
        textChinese: '',
        mp3Thai: { file: null, fileName: '', url: '' },
        mp3English: { file: null, fileName: '', url: '' },
        mp3Chinese: { file: null, fileName: '', url: '' },
        geminiMp3Thai: { file: null, fileName: '', url: '' },
        geminiMp3English: { file: null, fileName: '', url: '' },
        geminiMp3Chinese: { file: null, fileName: '', url: '' },
        createdAt: null,
      };
    },

    createEmptyTaxPrefixPattern() {
      return {
        uid: this.createTaxPrefixUid(),
        id: null,
        patternName: '',
        effectiveStartDate: '',
        effectiveEndDate: '',
        isActive: true,
        isNew: true,
        createdAt: null,
        steps: [this.createEmptyTaxPrefixStep()],
      };
    },

    addTaxPrefixPattern() {
      const pattern = this.createEmptyTaxPrefixPattern();
      this.taxPrefixPatterns.push(pattern);
      this.expandedTaxPrefixPatterns.push(pattern.uid);
    },

    removeTaxPrefixPattern(pattern) {
      const index = this.taxPrefixPatterns.indexOf(pattern);
      if (index === -1) return;

      this.stopTaxPrefixVoiceMp3();
      pattern.steps.forEach((step) => this.revokeTaxPrefixStepBlobUrls(step));

      if (!pattern.isNew && pattern.id && !this.taxPrefixPatternsToDelete.includes(pattern.id)) {
        this.taxPrefixPatternsToDelete.push(pattern.id);
      }
      this.taxPrefixPatterns.splice(index, 1);
      this.expandedTaxPrefixPatterns = this.expandedTaxPrefixPatterns.filter((uid) => uid !== pattern.uid);
    },

    addTaxPrefixStep(pattern) {
      pattern.steps.push(this.createEmptyTaxPrefixStep());
    },

    removeTaxPrefixStep(pattern, stepIndex) {
      const step = pattern.steps[stepIndex];
      if (!step) return;
      this.stopTaxPrefixVoiceMp3();
      this.revokeTaxPrefixStepBlobUrls(step);
      pattern.steps.splice(stepIndex, 1);
    },

    moveTaxPrefixStep(pattern, stepIndex, direction) {
      const target = stepIndex + direction;
      if (target < 0 || target >= pattern.steps.length) return;
      this.stopTaxPrefixVoiceMp3();
      const [step] = pattern.steps.splice(stepIndex, 1);
      pattern.steps.splice(target, 0, step);
    },

    revokeTaxPrefixStepBlobUrls(step) {
      MP3_SLOTS.forEach(({ prop }) => {
        if (step?.[prop]?.url?.startsWith('blob:')) {
          URL.revokeObjectURL(step[prop].url);
        }
      });
    },

    // ===== MP3 pick / preview =====

    triggerTaxPrefixStepMp3Upload(pattern, stepIndex, language, variant = 'default') {
      this.taxPrefixStepMp3UploadTarget = { pattern, stepIndex, language, variant };
      const input = this.$refs.taxPrefixStepMp3Input;
      if (input) {
        input.value = '';
        input.click();
      }
    },

    handleTaxPrefixStepMp3Upload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const target = this.taxPrefixStepMp3UploadTarget;
      if (!target) return;

      const resetInput = () => {
        this.taxPrefixStepMp3UploadTarget = null;
        if (this.$refs.taxPrefixStepMp3Input) this.$refs.taxPrefixStepMp3Input.value = '';
      };

      // Validate file type
      if (!file.name.toLowerCase().endsWith('.mp3') && file.type !== 'audio/mpeg') {
        this.$swal.fire({
          title: this.$t('error'),
          text: 'Please select a valid MP3 file.',
          icon: 'error',
          confirmButtonText: this.$t('ok'),
        });
        resetInput();
        return;
      }

      // Validate file size (max 10MB)
      const maxBytes = 10 * 1024 * 1024;
      if (file.size > maxBytes) {
        this.$swal.fire({
          title: this.$t('error'),
          text: 'File size must not exceed 10MB.',
          icon: 'error',
          confirmButtonText: this.$t('ok'),
        });
        resetInput();
        return;
      }

      const prop = this.mp3Prop(target.language, target.variant);
      const step = target.pattern?.steps?.[target.stepIndex];
      if (prop && step) {
        // Revoke previous blob URL if any
        if (step[prop]?.url?.startsWith('blob:')) {
          URL.revokeObjectURL(step[prop].url);
        }
        step[prop] = {
          file: file,
          fileName: file.name,
          url: URL.createObjectURL(file),
        };
      }

      resetInput();
    },

    removeTaxPrefixStepMp3(pattern, stepIndex, language, variant = 'default') {
      const prop = this.mp3Prop(language, variant);
      const step = pattern?.steps?.[stepIndex];
      if (prop && step && step[prop]) {
        if (step[prop].url && step[prop].url.startsWith('blob:')) {
          URL.revokeObjectURL(step[prop].url);
        }
        step[prop] = { file: null, fileName: '', url: '' };
      }

      // Stop playback if currently playing this file
      if (this.taxPrefixVoicePlaying === `${pattern.uid}_${stepIndex}_${language}_${variant}`) {
        this.stopTaxPrefixVoiceMp3();
      }
    },

    playTaxPrefixStepMp3(pattern, stepIndex, language, variant = 'default') {
      const key = `${pattern.uid}_${stepIndex}_${language}_${variant}`;

      // If already playing this one, stop it
      if (this.taxPrefixVoicePlaying === key) {
        this.stopTaxPrefixVoiceMp3();
        return;
      }

      // Stop any current playback
      this.stopTaxPrefixVoiceMp3();

      const prop = this.mp3Prop(language, variant);
      const step = pattern?.steps?.[stepIndex];
      if (!prop || !step || !step[prop]?.url) return;

      const audio = new Audio(step[prop].url);
      audio.addEventListener('ended', () => {
        this.taxPrefixVoicePlaying = null;
        this.taxPrefixVoiceAudio = null;
      });
      audio.addEventListener('error', () => {
        this.taxPrefixVoicePlaying = null;
        this.taxPrefixVoiceAudio = null;
      });
      audio.play();
      this.taxPrefixVoiceAudio = audio;
      this.taxPrefixVoicePlaying = key;
    },

    stopTaxPrefixVoiceMp3() {
      if (this.taxPrefixVoiceAudio) {
        this.taxPrefixVoiceAudio.pause();
        this.taxPrefixVoiceAudio.currentTime = 0;
        this.taxPrefixVoiceAudio = null;
      }
      this.taxPrefixVoicePlaying = null;
    },

    // ===== Validation + save (called by the parent page) =====

    validateTaxPrefixPatterns() {
      const errors = [];
      this.taxPrefixPatterns.forEach((pattern, pIdx) => {
        // Fully-untouched new patterns are skipped on save, so don't validate them
        if (!this.hasTaxPrefixPatternContent(pattern)) return;

        if (!this.validateDateRange(pattern.effectiveStartDate, pattern.effectiveEndDate)) {
          errors.push(`Tax Prefix Pattern ${pIdx + 1}: End Date must be on or after Start Date`);
        }

        pattern.steps.forEach((step, sIdx) => {
          const missing = [];
          LANGUAGES.forEach((lang) => {
            if (!step[lang.textProp]?.trim()) missing.push(`${lang.label} text`);
          });
          // Only the default voice is mandatory — the Gemini variant stays optional.
          MP3_SLOTS.filter((slot) => slot.required).forEach((slot) => {
            if (!step[slot.prop]?.fileName) missing.push(`${slot.label} MP3`);
          });
          if (missing.length > 0) {
            errors.push(`Tax Prefix Pattern ${pIdx + 1}, Step ${sIdx + 1}: ${missing.join(', ')} required`);
          }
        });
      });
      return errors;
    },

    hasTaxPrefixPatternContent(pattern) {
      if (pattern.patternName || pattern.effectiveStartDate || pattern.effectiveEndDate) return true;
      return pattern.steps.some(
        (step) =>
          LANGUAGES.some((lang) => !!step[lang.textProp]) ||
          MP3_SLOTS.some(({ prop }) => !!step[prop]?.fileName || !!step[prop]?.url || !!step[prop]?.file)
      );
    },

    buildTaxPrefixPatternPayload(pattern, index) {
      const mp3Url = (slot) => (slot?.url && !slot.url.startsWith('blob:') ? slot.url : null);

      return {
        ID: pattern.isNew ? null : pattern.id || null,
        PatternNumber: index + 1,
        PatternName: pattern.patternName || '',
        EffectiveStartDate: this.formatDateForAPI(pattern.effectiveStartDate),
        EffectiveEndDate: this.formatDateForAPI(pattern.effectiveEndDate),
        IsActive: pattern.isActive,
        Steps: pattern.steps.map((step) => ({
          ID: step.id || null,
          DurationSec: step.durationSec || 3,
          TextThai: step.textThai || '',
          TextEnglish: step.textEnglish || '',
          TextChinese: step.textChinese || '',
          Mp3ThaiUrl: mp3Url(step.mp3Thai),
          Mp3ThaiFileName: step.mp3Thai?.fileName || null,
          Mp3EnglishUrl: mp3Url(step.mp3English),
          Mp3EnglishFileName: step.mp3English?.fileName || null,
          Mp3ChineseUrl: mp3Url(step.mp3Chinese),
          Mp3ChineseFileName: step.mp3Chinese?.fileName || null,
          GeminiMp3ThaiUrl: mp3Url(step.geminiMp3Thai),
          GeminiMp3ThaiFileName: step.geminiMp3Thai?.fileName || null,
          GeminiMp3EnglishUrl: mp3Url(step.geminiMp3English),
          GeminiMp3EnglishFileName: step.geminiMp3English?.fileName || null,
          GeminiMp3ChineseUrl: mp3Url(step.geminiMp3Chinese),
          GeminiMp3ChineseFileName: step.geminiMp3Chinese?.fileName || null,
        })),
      };
    },

    /**
     * Persist every pattern, then the queued deletes. Called by the parent page's Save.
     * Deletes run first so a removed pattern can't be re-saved by a stale index.
     */
    async saveTaxPrefixPatterns(configId) {
      if (this.taxPrefixPatternsToDelete.length > 0) {
        await this.deleteMarkedTaxPrefixPatterns();
      }

      for (let index = 0; index < this.taxPrefixPatterns.length; index++) {
        await this.saveTaxPrefixPattern(configId, this.taxPrefixPatterns[index], index);
      }
    },

    async saveTaxPrefixPattern(configId, pattern, index) {
      if (pattern.isNew && !this.hasTaxPrefixPatternContent(pattern)) return;

      const saveResponse = await new Promise((resolve, reject) => {
        this.$call.SaveTaxPrefixPattern(
          configId,
          this.buildTaxPrefixPatternPayload(pattern, index),
          (response) => resolve(response),
          (error) => {
            console.error('Error saving tax prefix pattern:', error);
            reject(error);
          }
        );
      });

      if (!this.isApiSuccess(saveResponse)) {
        throw new Error(saveResponse?.Message || saveResponse?.message || 'Failed to save tax prefix pattern.');
      }

      this.applySavedTaxPrefixPattern(pattern, saveResponse?.Data || saveResponse?.data);

      if (this.hasPendingTaxPrefixStepMp3(pattern)) {
        await this.uploadTaxPrefixStepPendingFiles(pattern);

        // Re-save so the uploaded blob-storage URLs are persisted on the steps
        const updateResponse = await new Promise((resolve, reject) => {
          this.$call.SaveTaxPrefixPattern(
            configId,
            this.buildTaxPrefixPatternPayload(pattern, index),
            (response) => resolve(response),
            (error) => {
              console.error('Error updating tax prefix step MP3 URLs:', error);
              reject(error);
            }
          );
        });

        if (!this.isApiSuccess(updateResponse)) {
          throw new Error(updateResponse?.Message || updateResponse?.message || 'Failed to update tax prefix step MP3 URLs.');
        }
      }
    },

    applySavedTaxPrefixPattern(pattern, savedPattern) {
      if (!savedPattern) return;
      pattern.id = savedPattern.id || savedPattern.ID || pattern.id;
      pattern.createdAt = savedPattern.createdAt || savedPattern.CreatedAt || pattern.createdAt;
      pattern.isNew = false;

      // Server preserves request order, so match saved steps back by array position
      const savedSteps = savedPattern.steps || savedPattern.Steps || [];
      pattern.steps.forEach((step, index) => {
        const saved = savedSteps[index];
        if (!saved) return;
        step.id = saved.id || saved.ID || step.id;
        step.createdAt = saved.createdAt || saved.CreatedAt || step.createdAt;
      });
    },

    hasPendingTaxPrefixStepMp3(pattern) {
      return pattern.steps.some((step) => MP3_SLOTS.some(({ prop }) => step[prop]?.file && step[prop]?.url?.startsWith('blob:')));
    },

    async uploadTaxPrefixStepPendingFiles(pattern) {
      for (const step of pattern.steps) {
        const hasPending = MP3_SLOTS.some(({ prop }) => step[prop]?.file && step[prop]?.url?.startsWith('blob:'));
        if (!hasPending) continue;

        // The blob path embeds the step's created date, so the step must already be persisted.
        if (!step.createdAt) {
          throw new Error('Tax prefix step CreatedAt is required before MP3 upload.');
        }

        for (const slot of MP3_SLOTS) {
          const mp3 = step[slot.prop];
          if (!mp3?.file || !mp3?.url?.startsWith('blob:')) continue;

          const formData = new FormData();
          formData.append('entity', mp3.file, mp3.file.name);

          await new Promise((resolve, reject) => {
            this.$call.UploadTaxPrefixMp3(
              { entity: formData, createdAt: step.createdAt, prefixField: slot.uploadField },
              (response) => {
                if (this.isApiSuccess(response) && (response.Data || response.data)) {
                  URL.revokeObjectURL(mp3.url);
                  mp3.url = response.Data || response.data;
                  mp3.file = null;
                  resolve();
                } else {
                  reject(new Error(response?.Message || response?.message || `Upload failed for ${slot.uploadField}`));
                }
              },
              (error) => reject(error)
            );
          });
        }
      }
    },

    async deleteMarkedTaxPrefixPatterns() {
      const deletePromises = this.taxPrefixPatternsToDelete.map((id) => {
        return new Promise((resolve, reject) => {
          this.$call.DeleteTaxPrefixPattern(
            id,
            (response) => {
              if (this.isApiSuccess(response)) {
                resolve(response);
              } else {
                reject(new Error(response?.Message || `Failed to delete tax prefix pattern ${id}.`));
              }
            },
            (error) => {
              console.error(`Error deleting tax prefix pattern ${id}:`, error);
              reject(error);
            }
          );
        });
      });

      await Promise.all(deletePromises);
      this.taxPrefixPatternsToDelete = [];
    },
  },
};
</script>

<style scoped>
.language-config-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.custom-input {
  width: 100%;
  height: 45px;
  padding: 8px 16px;
  border: 1px solid #bec2ca;
  border-radius: 16px;
  background-color: white;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease;
}

textarea.custom-input {
  height: auto;
  padding: 10px 16px;
}

.custom-input:focus {
  border-color: #643990;
  outline: none;
}

.custom-input::placeholder {
  color: #9e9e9e;
}

.custom-input:disabled {
  background-color: #f5f5f5;
  color: #9e9e9e;
}

.input-error {
  border-color: #c62828 !important;
}

.tax-prefix-file-name-col {
  font-size: 10px;
  color: #333;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  text-align: center;
}

.tax-step-upload-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  border: 2px dashed #e57373;
  background-color: #fff8f8;
  border-radius: 8px;
  padding: 10px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #c62828;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.tax-step-upload-box:hover {
  border-color: #c62828;
  background-color: #ffefef;
}
</style>
